"use client";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://platform.monkeyscode.local";

/** A promo offer entity used across admin promo components. */
export type PromoOffer = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  allowed_models: string[];
  trial_days: number;
  max_registrations: number | null;
  registered_count: number;
  registrations_remaining: number | null;
  starts_at: string;
  /** `null` = permanent offer (no end date). */
  ends_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
};

/**
 * Git connection status for the current user.
 *
 * The shape is a discriminated union in practice: when `connected` is false
 * the server includes `configured` (whether this deployment has a GitHub App
 * at all) and `connect_url`; when true it includes the grant details. A
 * `connected: false, configured: false` response means the server cannot
 * support git at all and the UI must not offer a Connect button.
 */
export type GitStatus = {
  connected: boolean;
  provider: string;
  login?: string | null;
  connected_at?: string;
  last_used_at?: string | null;
  configured?: boolean;
  connect_url?: string;
};

/**
 * Per-key limits as the platform returns them (ApiKeyService::restrictionsOf).
 * Empty lists and null values mean "no per-key limit".
 */
export type ApiKeyLimits = {
  allowed_models: string[];
  allowed_cidrs: string[];
  spend_limit_daily_micros: number | null;
  spend_limit_monthly_micros: number | null;
  rate_limit_rpm: number | null;
  rate_limit_tpm: number | null;
};

/**
 * Limits as sent on create / PATCH. Omit a field to leave it unchanged;
 * `null` removes it. Spend caps go as USD decimal strings (converted exactly
 * server-side), never floats of micros.
 */
export type ApiKeyLimitsInput = {
  allowed_models?: string[] | null;
  allowed_cidrs?: string[] | null;
  spend_limit_daily_usd?: string | null;
  spend_limit_monthly_usd?: string | null;
  rate_limit_rpm?: number | null;
  rate_limit_tpm?: number | null;
};

/** Structured API error with machine-readable code. */
export class ApiError extends Error {
  code: string;
  email?: string;
  use?: string;
  current_plan_code?: string;
  status?: number;
  /** Per-field validation messages (422), keyed by field name. */
  fields?: Record<string, string>;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.code = code || "unknown";
  }
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  token?: string | null;
  baseUrl?: string;
}

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("mc_token");
  }

  async request<T = unknown>(path: string, options: ApiOptions & { _retried?: boolean } = {}): Promise<T> {
    const { method = "GET", body, headers = {}, token, baseUrl, _retried } = options;
    const authToken = token ?? this.getToken();
    const url = baseUrl || this.baseUrl;

    const res = await fetch(`${url}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !_retried && authToken) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return this.request<T>(path, { ...options, token: this.getToken(), _retried: true });
      }
      if (typeof window !== "undefined") {
        localStorage.removeItem("mc_token");
        localStorage.removeItem("mc_refresh");
        localStorage.removeItem("mc_admin_token");
        localStorage.removeItem("mc_admin_refresh");
        localStorage.removeItem("mc_admin_name");
        localStorage.removeItem("mc_admin_email");
        localStorage.removeItem("mc_impersonating");
        document.cookie = "mc_session=; path=/; max-age=0";
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- API interceptor, no router available
        window.location.href = "/login";
      }
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      const apiErr = new ApiError(err.message || err.error || "Request failed", err.error);
      apiErr.email = err.email;
      apiErr.use = err.use;
      apiErr.current_plan_code = err.current_plan_code;
      apiErr.status = res.status;
      if (err.errors && typeof err.errors === "object" && !Array.isArray(err.errors)) {
        apiErr.fields = err.errors;
      }
      throw apiErr;
    }

    return res.json();
  }

  private async refreshToken(): Promise<boolean> {
    // If a refresh is already in-flight, wait for it instead of firing a second one
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async _doRefresh(): Promise<boolean> {
    const refresh = typeof window !== "undefined" ? localStorage.getItem("mc_refresh") : null;
    if (!refresh) return false;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const res = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (!res.ok) return false;

      const data = await res.json();
      if (typeof window !== "undefined" && data.access_token) {
        localStorage.setItem("mc_token", data.access_token);
        if (data.refresh_token) {
          localStorage.setItem("mc_refresh", data.refresh_token);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // ── Auth ────────────────────────────────────────────────────────

  login(email: string, password: string, recaptchaToken?: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
      user: { id: number; email: string; name: string };
    }>("/v1/auth/login", {
      method: "POST",
      body: {
        email,
        password,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  register(name: string, email: string, password: string, recaptchaToken?: string) {
    return this.request<{ message: string; email: string; needs_verification: boolean }>("/v1/auth/register", {
      method: "POST",
      body: {
        name,
        email,
        password,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  forgotPassword(email: string, recaptchaToken?: string) {
    return this.request<{ message: string }>("/v1/auth/forgot-password", {
      method: "POST",
      body: {
        email,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  resetPassword(token: string, password: string, recaptchaToken?: string) {
    return this.request<{ message: string }>("/v1/auth/reset-password", {
      method: "POST",
      body: {
        token,
        password,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  verifyEmail(code: string, recaptchaToken?: string) {
    return this.request<{
      message: string;
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    }>("/v1/auth/verify-email", {
      method: "POST",
      body: {
        code,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  resendVerification(email: string, recaptchaToken?: string) {
    return this.request<{ message: string }>("/v1/auth/resend-verification", {
      method: "POST",
      body: {
        email,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  // ── OAuth ───────────────────────────────────────────────────

  /** Get the GitHub OAuth redirect URL. */
  githubRedirect() {
    return this.request<{ redirect_url: string }>("/v1/auth/github");
  }

  /** Exchange GitHub authorization code for JWT tokens. */
  githubCallback(code: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      is_new_user: boolean;
      user: { id: number; email: string; name: string; org_id: number | null };
    }>("/v1/auth/github/callback", {
      method: "POST",
      body: { code },
    });
  }

  /** Get the Google OAuth redirect URL. */
  googleRedirect() {
    return this.request<{ redirect_url: string }>("/v1/auth/google");
  }

  // ── Git integration ─────────────────────────────────────────
  //
  // DISTINCT FROM githubRedirect()/githubCallback() ABOVE.
  // Those are GitHub *login* (identity, scope=user:email). These are the
  // GitHub *App installation* that grants push access to repositories — a
  // different consent with a different blast radius. Conflating the two is
  // exactly the mistake the backend design rejected: a login token must
  // never be push-capable.

  /**
   * Start the git connect flow.
   *
   * Returns the GitHub App installation URL plus the signed, single-use
   * `state` bound to this user. Redirect the browser to `redirect_url`.
   * 503 when the server has no GitHub App configured.
   */
  gitConnect() {
    return this.request<{ redirect_url: string; state: string }>("/v1/git/connect");
  }

  /**
   * Complete the connect flow after GitHub redirects back.
   *
   * `state` is CONSUMED server-side (single-use), so a failed callback
   * cannot be retried — the caller must restart from gitConnect().
   */
  gitCallback(params: { code: string; state: string; installation_id?: string }) {
    return this.request<{ connected: boolean; provider: string; login: string }>(
      "/v1/git/callback",
      { method: "POST", body: params },
    );
  }

  /** Current user's git connection status. Never returns secret material. */
  gitStatus() {
    return this.request<GitStatus>("/v1/git/status");
  }

  /** Disconnect the git provider. Deactivates the grant and purges cached tokens. */
  gitDisconnect() {
    return this.request<{ connected: boolean }>("/v1/git/disconnect", {
      method: "POST",
    });
  }

  /** List repos the user granted access to via the GitHub App. */
  gitRepos() {
    return this.request<{
      repos: Array<{
        full_name: string;
        name: string;
        private: boolean;
        default_branch: string;
        html_url: string;
      }>;
      connected: boolean;
      login?: string;
    }>("/v1/git/repos");
  }

  // ── Admin: git integration (super admin) ────────────────────

  /** Fleet-wide git summary over a trailing window of `days`. */
  adminGitSummary(days = 7) {
    return this.request<AdminGitSummary>(`/v1/admin/git/summary?days=${days}`);
  }

  /** Reconciler health. `abort_is_latest` is the alarm condition. */
  adminGitHealth() {
    return this.request<AdminGitHealth>("/v1/admin/git/health");
  }

  /** Recent git audit events. */
  adminGitEvents(opts: { action?: string; userId?: number; limit?: number; offset?: number } = {}) {
    const params = new URLSearchParams();
    if (opts.action) params.set("action", opts.action);
    if (opts.userId) params.set("user_id", String(opts.userId));
    params.set("limit", String(opts.limit ?? 50));
    params.set("offset", String(opts.offset ?? 0));

    return this.request<AdminGitEvents>(`/v1/admin/git/events?${params}`);
  }

  // ── Feedback survey (public, token-authorised) ────────────────

  /**
   * Load the survey behind an emailed token. No login needed — the token is
   * the credential. Stamps the first open server-side.
   */
  getFeedbackSurvey(token: string) {
    // `token: ""` suppresses the Authorization header. The survey token is
    // the only credential, and sending a stale session bearer could trip the
    // 401 → redirect-to-login path for a user who just clicked an email.
    return this.request<FeedbackSurveyPayload>(
      `/v1/feedback/survey/${encodeURIComponent(token)}`,
      { token: "" },
    );
  }

  /** Submit answers once. 409 `already_completed` on a repeat; 422 carries `fields`. */
  submitFeedbackSurvey(token: string, answers: FeedbackSurveyAnswers) {
    return this.request<FeedbackSurveyPayload & { message: string }>(
      `/v1/feedback/survey/${encodeURIComponent(token)}`,
      { method: "POST", body: answers, token: "" },
    );
  }

  // ── Admin: feedback survey results (super admin) ──────────────

  /** Aggregates. Omit from/to for all time. */
  adminFeedbackSummary(opts: { from?: string; to?: string; groupBy?: "day" | "month" } = {}) {
    const params = new URLSearchParams();
    if (opts.from) params.set("from", opts.from);
    if (opts.to) params.set("to", opts.to);
    params.set("group_by", opts.groupBy ?? "day");
    return this.request<AdminFeedbackSummary>(`/v1/admin/feedback/summary?${params}`);
  }

  adminFeedbackResponses(opts: {
    status?: "completed" | "pending" | "all";
    from?: string;
    to?: string;
    q?: string;
    hadIssue?: boolean;
    limit?: number;
    offset?: number;
  } = {}) {
    const params = new URLSearchParams();
    params.set("status", opts.status ?? "completed");
    if (opts.from) params.set("from", opts.from);
    if (opts.to) params.set("to", opts.to);
    if (opts.q) params.set("q", opts.q);
    if (opts.hadIssue !== undefined) params.set("had_issue", opts.hadIssue ? "1" : "0");
    params.set("limit", String(opts.limit ?? 25));
    params.set("offset", String(opts.offset ?? 0));
    return this.request<AdminFeedbackResponses>(`/v1/admin/feedback/responses?${params}`);
  }

  adminFeedbackResponse(id: number) {
    return this.request<AdminFeedbackResponseDetail>(`/v1/admin/feedback/responses/${id}`);
  }

  /**
   * Platform compliance log (`audit_logs`).
   *
   * NOTE: this is `/v1/admin/audit-log`, which is NOT the same endpoint as
   * `/v1/admin/audit` — the latter is served by model-proxy and returns HTTP
   * request logs. `action` is prefix-matched, so "git." returns all git events.
   */
  adminAuditLog(opts: {
    action?: string;
    userId?: number;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    const params = new URLSearchParams();
    if (opts.action) params.set("action", opts.action);
    if (opts.userId) params.set("user_id", String(opts.userId));
    if (opts.from) params.set("from", opts.from);
    if (opts.to) params.set("to", opts.to);
    params.set("limit", String(opts.limit ?? 50));
    params.set("offset", String(opts.offset ?? 0));

    return this.request<AdminAuditLogResponse>(`/v1/admin/audit-log?${params}`);
  }

  /** Exchange Google authorization code for JWT tokens. */
  googleCallback(code: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      is_new_user: boolean;
      user: { id: number; email: string; name: string; org_id: number | null };
    }>("/v1/auth/google/callback", {
      method: "POST",
      body: { code },
    });
  }

  me() {
    return this.request<{
      id: number;
      email: string;
      name: string;
      avatar_url: string | null;
      onboarding_completed: boolean;
      account_type: string;
      orgs: Array<{
        id: number;
        name: string;
        role: string;
        account_type: string;
      }>;
    }>("/v1/auth/me");
  }

  approveDevice(userCode: string) {
    return this.request<{ message: string }>("/v1/auth/device/approve", {
      method: "POST",
      body: { user_code: userCode },
    });
  }

  approveDeviceSession(session: string) {
    return this.request<{ message: string }>("/v1/auth/device/approve-session", {
      method: "POST",
      body: { session },
    });
  }

  // ── Rate Limits ──────────────────────────────────────────────────

  async rateLimits() {
    // Rate limits live on model-proxy, not platform
    const modelProxyUrl = process.env.NEXT_PUBLIC_MODEL_PROXY_URL || "http://localhost:8090";
    // Get current user ID for the X-User-ID header
    const me = await this.me().catch(() => null);
    const orgs = me?.orgs || [];
    const company = orgs.find((o) => o.account_type === "company");
    const personal = orgs.find((o) => (o.account_type || "individual") === "individual");
    const activeOrg = company ?? personal ?? orgs[0];
    return this.request<{
      user_id: string;
      plan: string;
      pools: {
        // Per-model 5h burst pools (independent counters), keyed by canonical model.
        models?: Record<string, {
          limit: number; used: number; remaining: number;
          reset_at?: string; seconds_remaining?: number; pool?: string;
        }>;
        queue_class?: string;
      };
      generation_models: Record<string, {
        requests: { limit: number; used: number; remaining: number };
        cycle_start: string;
        cycle_reset: string;
        seconds_remaining: number;
      }>;
    }>(`/v1/rate-limits`, {
      baseUrl: modelProxyUrl,
      headers: {
        "X-User-ID": me?.id ? String(me.id) : "0",
        "X-Org-ID": activeOrg?.id ? String(activeOrg.id) : "",
      },
    });
  }

  // ── Usage ────────────────────────────────────────────────────────

  usageDashboard(period: string = "7d", page = 1, perPage = 20) {
    const params = new URLSearchParams({ period, page: String(page), per_page: String(perPage) });
    return this.request<{
      period: string;
      from: string;
      to: string;
      summary: {
        total_requests: number;
        total_tokens_in: number;
        total_tokens_out: number;
        total_cached_tokens: number;
        total_cost_micros: number;
        avg_duration_ms: number;
        avg_tok_per_sec: number;
        error_count: number;
        error_rate: number;
        change: { requests: number; tokens: number };
      };
      timeseries: Array<{
        bucket: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cached_tokens: number;
        cost_micros: number;
      }>;
      by_model: Array<{
        model: string;
        provider: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cached_tokens: number;
        cost_micros: number;
      }>;
      requests: {
        data: Array<{
          id: number;
          event_id: string;
          model: string;
          provider: string;
          kind: string;
          tokens_in: number;
          tokens_out: number;
          cached_tokens: number;
          tok_per_sec: number;
          cost_micros: number;
          duration_ms: number;
          status: string;
          occurred_at: string;
        }>;
        total: number;
        page: number;
        per_page: number;
        total_pages: number;
      };
    }>(`/v1/usage/dashboard?${params}`);
  }

  /** Auto mode usage card (build step 9): tasks on Auto this week, frontier step-ins, Premium pool. */
  autoUsageSummary() {
    return this.request<import("@/lib/planRequired").AutoUsageSummary>("/v1/usage/auto-summary");
  }

  orgUsageDashboard(orgId: number, period = "7d", page = 1, perPage = 20, userId?: number) {
    const params = new URLSearchParams({ period, page: String(page), per_page: String(perPage) });
    if (userId) params.set("user_id", String(userId));
    return this.request<{
      org_id: number;
      period: string;
      from: string;
      to: string;
      filter_user_id: number | null;
      summary: {
        total_requests: number;
        total_tokens_in: number;
        total_tokens_out: number;
        total_cached_tokens: number;
        total_cost_micros: number;
        avg_duration_ms: number;
        avg_tok_per_sec: number;
        error_count: number;
        error_rate: number;
        change: { requests: number; tokens: number };
      };
      timeseries: Array<{
        bucket: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cached_tokens: number;
        cost_micros: number;
      }>;
      by_model: Array<{
        model: string;
        provider: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cached_tokens: number;
        cost_micros: number;
      }>;
      by_user: Array<{
        user_id: number;
        user_name: string;
        user_email: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cached_tokens: number;
        cost_micros: number;
      }>;
      requests: {
        data: Array<{
          id: number;
          event_id: string;
          user_id: number;
          user_name: string;
          model: string;
          provider: string;
          kind: string;
          tokens_in: number;
          tokens_out: number;
          cached_tokens: number;
          tok_per_sec: number;
          cost_micros: number;
          duration_ms: number;
          status: string;
          occurred_at: string;
        }>;
        total: number;
        page: number;
        per_page: number;
        total_pages: number;
      };
    }>(`/v1/orgs/${orgId}/usage?${params}`);
  }

  // ── Billing ──────────────────────────────────────────────────

  getPlans(accountType?: string) {
    const params = accountType ? `?account_type=${accountType}` : "";
    return this.request<Array<{
      id: number;
      code: string;
      name: string;
      account_type: string;
      price_cents: number;
      is_per_seat: boolean;
      max_seats: number;
      features: string;
      limits: string;
      stripe_price_id?: string;
    }>>(`/v1/billing/plans${params}`, { baseUrl: BILLING_URL });
  }

  createCheckout(data: {
    org_id: number;
    plan_code: string;
    seats?: number;
    email: string;
    name: string;
    success_url: string;
    cancel_url: string;
  }) {
    return this.request<{ session_id: string; checkout_url: string } | { subscription_id: number; status: string; free: boolean }>(
      "/v1/billing-proxy/checkout",
      { method: "POST", body: data },
    );
  }

  createSubscription(data: {
    org_id: number;
    plan_code: string;
    seats: number;
    email: string;
    name: string;
  }) {
    return this.request<{ subscription_id: string; client_secret: string } | { subscription_id: number; status: string; free: boolean }>(
      "/v1/onboarding/subscribe",
      { method: "POST", body: data },
    );
  }

  getSubscription(orgId: number) {
    return this.request<{
      org_id: number;
      plan?: { code: string; name: string; price_cents: number };
      status: string;
      seats: number;
      current_period_end?: string;
      cancel_at_period_end?: boolean;
      /** First paid subscribe allowed — use Checkout */
      can_checkout?: boolean;
      /** Already paid — use change-plan / portal only */
      must_use_change_plan?: boolean;
      /** Linked to Stripe — show Manage billing */
      has_stripe_subscription?: boolean;
    }>(`/v1/billing/subscription?org_id=${orgId}`, { baseUrl: BILLING_URL });
  }

  createPortalSession(orgId: number, returnUrl: string) {
    return this.request<{ portal_url: string }>(
      "/v1/billing-proxy/portal",
      { method: "POST", body: { org_id: orgId, return_url: returnUrl } },
    );
  }

  /** Cancel an incomplete subscription (user abandoned payment form). */
  cancelIncompleteSubscription(orgId: number) {
    return this.request<{ ok: boolean; status: string }>(
      "/v1/billing-proxy/cancel-incomplete",
      { method: "POST", body: { org_id: orgId } },
    );
  }

  // ── Onboarding ───────────────────────────────────────────────

  getOnboardingStatus() {
    return this.request<{
      onboarding_completed: boolean;
      account_type: string;
      org_id: number | null;
      org_name: string | null;
      pending_invites: Array<{
        id: number;
        org_id: number;
        org_name: string;
        role: string;
        email: string;
      }>;
      domain_orgs: Array<{
        org_id: number;
        org_name: string;
        domain: string;
      }>;
    }>("/v1/onboarding/status");
  }

  setAccountType(accountType: "individual" | "company", orgName?: string) {
    return this.request<{ account_type: string; org_id: number }>(
      "/v1/onboarding/account-type",
      { method: "POST", body: { account_type: accountType, org_name: orgName } },
    );
  }

  completeOnboarding() {
    return this.request<{ onboarding_completed: boolean }>(
      "/v1/onboarding/complete",
      { method: "POST", body: {} },
    );
  }

  acceptInvite(inviteId: number) {
    return this.request<{ joined: boolean; org_id: number; org_name: string; role: string }>(
      "/v1/onboarding/accept-invite",
      { method: "POST", body: { invite_id: inviteId } },
    );
  }

  acceptDomainJoin(orgId: number) {
    return this.request<{ joined: boolean; org_id: number; org_name: string; role: string }>(
      "/v1/onboarding/accept-domain-join",
      { method: "POST", body: { org_id: orgId } },
    );
  }

  rejectInvites() {
    return this.request<{ rejected: boolean }>(
      "/v1/onboarding/reject-invites",
      { method: "POST", body: {} },
    );
  }

  setOrgDomains(orgId: number, domains: string[]) {
    return this.request<{ allowed_domains: string[] }>(
      "/v1/onboarding/set-org-domains",
      { method: "POST", body: { org_id: orgId, domains } },
    );
  }

  // ── Profile Management ──────────────────────────────────────────

  updateProfile(data: { name?: string }) {
    return this.request<{ id: number; name: string }>(
      "/v1/auth/me",
      { method: "PATCH", body: data },
    );
  }

  // ── Organization Management ─────────────────────────────────────

  getOrg(orgId: number) {
    return this.request<{
      id: number;
      name: string;
      slug: string;
      account_type: string;
      allowed_domains: string[] | null;
      logo_url: string | null;
      status: string;
    }>(`/v1/orgs/${orgId}`);
  }

  updateOrg(orgId: number, data: { name?: string; allowed_domains?: string[] }) {
    return this.request<{ id: number; name: string }>(
      `/v1/orgs/${orgId}`,
      { method: "PATCH", body: data },
    );
  }

  getOrgMembers(orgId: number) {
    return this.request<Array<{
      id: number;
      user_id: number;
      name: string;
      email: string;
      role: string;
      avatar_url: string | null;
      joined_at: string;
    }>>(`/v1/orgs/${orgId}/members`);
  }

  getOrgInvites(orgId: number) {
    return this.request<{
      invites: Array<{
        id: number;
        email: string;
        role: string;
        token: string | null;
        created_at: string;
        expires_at: string;
      }>;
    }>(`/v1/orgs/${orgId}/invites`);
  }

  updateMemberRole(orgId: number, memberId: number, role: string) {
    return this.request<{ role: string }>(
      `/v1/orgs/${orgId}/members/${memberId}`,
      { method: "PATCH", body: { role } },
    );
  }

  removeMember(orgId: number, memberId: number) {
    return this.request<{ removed: boolean }>(
      `/v1/orgs/${orgId}/members/${memberId}`,
      { method: "DELETE" },
    );
  }

  updateBillingSeats(orgId: number, seats: number) {
    return this.request<{ org_id: number; seats: number }>(
      "/v1/billing-proxy/update-seats",
      { method: "POST", body: { org_id: orgId, seats } },
    );
  }

  changePlan(orgId: number, planCode: string) {
    return this.request<{ message: string; plan: { code: string; name: string; price_cents: number }; status: string }>(
      "/v1/billing-proxy/change-plan",
      { method: "POST", body: { org_id: orgId, plan_code: planCode } },
    );
  }

  // ── Credits Store (Platform proxy) ───────────────────────────────

  /** List public credit packs (platform proxy). */
  listCreditPacks() {
    return this.request<{
      packs: Array<{
        id: number;
        code: string;
        name: string;
        description: string;
        credits_cents: number;
        price_cents: number;
        is_public: boolean;
      }>;
    }>("/v1/credits/packs");
  }

  /** Get my or org credit balance.
   *  scope=me → primary wallet (+ balances_by_org, credits_remaining_cents_all_orgs).
   *  scope=org → { org_id, balances[] } for every member wallet in the org.
   */
  getCreditBalance(params: { scope: "me" | "org"; org_id?: number }) {
    const q = new URLSearchParams({ scope: params.scope });
    if (params.org_id) q.set("org_id", String(params.org_id));
    return this.request<
      | {
          org_id: number | null;
          user_id: number | null;
          credits_total_cents: number;
          credits_used_cents: number;
          credits_remaining_cents: number;
          currency: string;
          updated_at: string | null;
          /** Every (org_id, user_id) wallet for the authenticated user. */
          balances_by_org?: Array<{
            org_id: number;
            user_id: number;
            credits_total_cents: number;
            credits_used_cents: number;
            credits_remaining_cents: number;
            currency?: string;
            updated_at?: string | null;
          }>;
          credits_remaining_cents_all_orgs?: number;
        }
      | {
          org_id: number;
          balances: Array<{
            org_id: number;
            user_id: number;
            credits_total_cents: number;
            credits_used_cents: number;
            credits_remaining_cents: number;
            currency: string;
            updated_at: string | null;
          }>;
        }
    >(`/v1/credits/balance?${q}`);
  }

  /** Create a credit pack checkout. */
  createCreditCheckout(data: {
    org_id: number;
    pack_id: number;
    user_id?: number;
    idempotency_key?: string;
  }) {
    return this.request<{
      purchase_id: number;
      status: string;
      fake?: boolean;
      pack: { id: number; code: string; credits_cents: number; price_cents: number };
      stripe?: { payment_intent_id: string; client_secret: string };
    }>("/v1/credits/checkout", { method: "POST", body: data });
  }

  /** Org-admin batch checkout — one PaymentIntent for pack × N members. */
  createCreditCheckoutBatch(data: {
    org_id: number;
    pack_id: number;
    user_ids: number[];
    idempotency_key?: string;
  }) {
    return this.request<{
      purchase_id: number;
      status: string;
      fake?: boolean;
      member_count?: number;
      unit_price_cents?: number;
      total_price_cents?: number;
      credits_cents_per_user?: number;
      user_ids?: number[];
      pack?: { id: number; code: string; credits_cents: number; price_cents: number };
      stripe?: { payment_intent_id: string; client_secret: string };
    }>("/v1/credits/checkout-batch", { method: "POST", body: data });
  }

  /** Get credit purchase status. */
  getCreditPurchase(id: number) {
    return this.request<Record<string, unknown>>(`/v1/credits/purchases/${id}`);
  }

  /** List credit transactions.
   *  Own history defaults to all orgs (company purchases appear even if UI is on personal workspace).
   *  Pass org_scoped: true with org_id to filter to one org.
   */
  getCreditTransactions(params: {
    org_id?: number;
    user_id?: number;
    org_scoped?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const q = new URLSearchParams();
    if (params.org_id) q.set("org_id", String(params.org_id));
    if (params.user_id) q.set("user_id", String(params.user_id));
    if (params.org_scoped) q.set("org_scoped", "1");
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return this.request<{
      transactions: Array<{
        id: number;
        public_id?: string;
        org_id: number | null;
        user_id: number;
        delta_cents: number;
        balance_after_cents: number;
        type: string;
        reference_type: string | null;
        reference_id: string | null;
        actor_user_id: number | null;
        reason: string | null;
        created_at: string;
      }>;
    }>(`/v1/credits/transactions?${q}`);
  }

  // ── Admin Credits (Super Admin) ─────────────────────────────────

  /** List credit balances (super admin). */
  adminListCredits(params?: { org_id?: number; user_id?: number; limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.org_id) q.set("org_id", String(params.org_id));
    if (params?.user_id) q.set("user_id", String(params.user_id));
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{ credits: Array<Record<string, unknown>> }>(`/v1/admin/credits${qs}`);
  }

  /** Grant bonus credits (super admin). */
  adminGrantCredits(data: {
    org_id?: number;
    user_id: number;
    amount_cents: number;
    reason: string;
    idempotency_key: string;
    notify_user?: boolean;
  }) {
    return this.request<{
      ledger_id?: string;
      user_id: number;
      org_id?: number;
      delta_cents: number;
      balance_remaining_cents: number;
      granted_by: number;
      reason: string;
    }>("/v1/admin/credits/grant", { method: "POST", body: data });
  }

  /** Override credit balance (super admin). */
  adminAdjustCredits(data: {
    org_id?: number;
    user_id: number;
    mode: "set_remaining" | "delta";
    amount_cents: number;
    reason: string;
    idempotency_key: string;
    confirm_phrase: string;
  }) {
    return this.request<Record<string, unknown>>("/v1/admin/credits/adjust", {
      method: "POST",
      body: data,
    });
  }

  /** List credit ledger (super admin). */
  adminListCreditLedger(params: { user_id: number; org_id?: number; limit?: number; offset?: number }) {
    const q = new URLSearchParams({ user_id: String(params.user_id) });
    if (params.org_id) q.set("org_id", String(params.org_id));
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return this.request<{ transactions: Array<Record<string, unknown>> }>(`/v1/admin/credits/ledger?${q}`);
  }

  getEntitlement(orgId: number, userId?: number) {
    const params = userId
      ? `org_id=${orgId}&user_id=${userId}`
      : `org_id=${orgId}`;
    return this.request<{
      allowed: boolean;
      source: string;
      reason?: string;
      plan?: string;
      plan_price_cents?: number;
      plan_is_per_seat?: boolean;
      message?: string;
      upgrade_url?: string;
      buy_credits_url?: string;
      plan_tokens_used: number;
      plan_tokens_limit: number;
      plan_tokens_remaining: number;
      credits_remaining_cents: number;
      allowed_models?: string[];
      promo_rate_limits?: boolean;
      // Monthly $ budget model (present when MONTHLY_BUDGET_MODE=on)
      budget_micros?: number;
      budget_spend_micros?: number;
      budget_remaining_micros?: number;
    }>(`/v1/billing/entitlement?${params}`, { baseUrl: BILLING_URL });
  }

  // ── Promo Campaigns (Super Admin) ──────────────────────────────

  /** List all promo offers (admin view — includes inactive/expired). */
  listPromoOffers(params?: {
    page?: number;
    per_page?: number;
    is_active?: boolean;
  }) {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.per_page) q.set("per_page", String(params.per_page));
    if (params?.is_active !== undefined) q.set("is_active", String(params.is_active));
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{
      data: Array<PromoOffer>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/v1/admin/promo/offers${qs}`);
  }

  /** Get a single promo offer by id. */
  getPromoOffer(id: number) {
    return this.request<{ offer: PromoOffer }>(`/v1/admin/promo/offers/${id}`);
  }

  /** Create a new promo offer. */
  createPromoOffer(data: {
    code: string;
    name: string;
    description?: string;
    allowed_models: string[];
    trial_days: number;
    max_registrations?: number | null;
    starts_at: string;
    /** `null` = permanent offer (no end date). */
    ends_at: string | null;
    is_active?: boolean;
  }) {
    return this.request<{ offer: PromoOffer }>("/v1/admin/promo/offers", {
      method: "POST",
      body: data,
    });
  }

  /** Update a promo offer. */
  updatePromoOffer(id: number, data: Partial<PromoOffer>) {
    return this.request<{ offer: PromoOffer }>(`/v1/admin/promo/offers/${id}`, {
      method: "PATCH",
      body: data,
    });
  }

  /** Archive (soft-delete) a promo offer. */
  archivePromoOffer(id: number) {
    return this.request<{ ok: boolean; message: string; offer: PromoOffer }>(
      `/v1/admin/promo/offers/${id}`,
      { method: "DELETE" },
    );
  }

  // ── Promo Registrations (Super Admin) ───────────────────────────

  /** List promo registrations with filtering + pagination. */
  listPromoRegistrations(params?: {
    page?: number;
    per_page?: number;
    offer_id?: number;
    status?: "active" | "expired" | "converted" | "cancelled";
    search?: string;
  }) {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.per_page) q.set("per_page", String(params.per_page));
    if (params?.offer_id) q.set("offer_id", String(params.offer_id));
    if (params?.status) q.set("status", params.status);
    if (params?.search) q.set("search", params.search);
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{
      data: Array<{
        user_id: number;
        name: string;
        email: string;
        trial_ends_at: string;
        status: string;
        converted_at: string | null;
        days_remaining: number;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/v1/admin/promo/registrations${qs}`);
  }

  // ── Promo Stats (Super Admin) ───────────────────────────────────

  /** Aggregated promo stats. */
  getPromoStats() {
    return this.request<{
      total_registrations: number;
      active: number;
      expired: number;
      converted: number;
      by_offer: Array<{ offer_code: string; count: number }>;
    }>("/v1/admin/promo/stats");
  }

  // ── Promo Registration (Public — rate-limited 3/60s) ────────────

  /** Register a user via promo code. */
  registerPromoUser(data: {
    name: string;
    email: string;
    password: string;
    promo_code: string;
    recaptcha_token?: string;
  }) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      user: { id: number; email: string; name: string };
      promo: { trial_ends_at: string; days_remaining: number };
    }>("/v1/promo/register", {
      method: "POST",
      body: data,
    });
  }

  // ── Promo Status (User-facing, any authenticated user) ───────────

  /** Get current user's promo trial status. */
  getPromoStatus() {
    return this.request<{
      is_promo_user: boolean;
      trial_ends_at: string | null;
      days_remaining: number;
      status?: "active" | "expired" | "converted" | "cancelled";
      allowed_models?: string[];
      offer_code?: string | null;
    }>("/v1/promo/status");
  }

  /** Activate a promo trial for the current authenticated user. */
  activatePromo(data: { promo_code: string }) {
    return this.request<{
      ok: boolean;
      promo: { trial_ends_at: string; days_remaining: number };
    }>("/v1/promo/activate", {
      method: "POST",
      body: data,
    });
  }

  // ── Public offers (no auth) ─────────────────────────────────────

  /** List active offers (public, no auth). */
  listPublicPromoOffers() {
    return this.request<{
      offers: Array<{
        code: string;
        name: string;
        description: string | null;
        trial_days: number;
        allowed_models: string[];
        starts_at: string;
        /** `null` = permanent offer (no claim deadline). */
        ends_at: string | null;
        registrations_remaining: number | null;
      }>;
    }>("/v1/promo/offers");
  }

  /** Get a single offer by code (public, no auth). */
  getPublicPromoOffer(code: string) {
    return this.request<{ offer: PromoOffer }>(`/v1/promo/offers/${code}`);
  }

  // ── Admin Workspaces ──────────────────────────────────────────

  /** List all cloud workspaces (super admin). */
  adminListWorkspaces(params?: { status?: string; org_id?: number; page?: number; per_page?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.org_id) qs.set("org_id", String(params.org_id));
    if (params?.page) qs.set("page", String(params.page));
    if (params?.per_page) qs.set("per_page", String(params.per_page));
    const q = qs.toString();
    return this.request<{
      data: Array<{
        uuid: string;
        name: string;
        status: string;
        compute_tier: string;
        region: string;
        total_minutes: number;
        credits_per_min: number;
        org_id: number;
        org_name: string;
        user_id: number;
        user_email: string;
        user_name: string;
        started_at: string | null;
        last_active_at: string | null;
        created_at: string | null;
      }>;
      total: number;
      page: number;
      per_page: number;
    }>(`/v1/admin/workspaces${q ? "?" + q : ""}`);
  }

  /** Workspace aggregate stats (super admin). */
  adminWorkspaceStats() {
    return this.request<{
      running: number;
      stopped: number;
      provisioning: number;
      deleted: number;
      total: number;
      total_minutes: number;
      top_orgs: Array<{ org_id: number; org_name: string; minutes: number }>;
    }>("/v1/admin/workspaces/stats");
  }

  /** Force-stop a workspace (super admin). */
  adminStopWorkspace(uuid: string) {
    return this.request<{ ok: boolean }>(`/v1/admin/workspaces/${uuid}/stop`, { method: "POST" });
  }

  /** Force-delete a workspace (super admin). */
  adminDeleteWorkspace(uuid: string) {
    return this.request<{ ok: boolean }>(`/v1/admin/workspaces/${uuid}`, { method: "DELETE" });
  }

  /** List workspaces for an org (org admin). */
  listOrgWorkspaces(orgId: number) {
    return this.request<Array<{
      id: string;
      name: string;
      region: string;
      status: string;
      compute_tier: string;
      total_minutes: number;
      user_id: number;
      started_at: string | null;
      last_active_at: string | null;
      created_at: string | null;
    }>>("/v1/workspaces/org", { headers: { "X-Org-Id": String(orgId) } });
  }

  /** Stop a workspace in the org (org admin). */
  stopOrgWorkspace(orgId: number, uuid: string) {
    return this.request<{ ok: boolean }>(`/v1/workspaces/org/${uuid}/stop`, {
      method: "POST",
      headers: { "X-Org-Id": String(orgId) },
    });
  }

  /** Create a workspace in the org. */
  createWorkspace(orgId: number, data: { name: string; compute_tier: string; repo_url?: string; branch?: string }) {
    return this.request<{ id: string; name: string; status: string }>("/v1/workspaces/org", {
      method: "POST",
      body: data,
      headers: { "X-Org-Id": String(orgId) },
    });
  }

  /** Alias for listOrgWorkspaces — used by the user-facing workspaces page. */
  listWorkspaces(orgId: number) {
    return this.listOrgWorkspaces(orgId);
  }

  /**
   * Per-org workspace feature flags, for deciding what the UI draws.
   *
   * `web_ide` gates the browser IDE and defaults off during staged rollout.
   * This is a presentation hint only — the enforced gate is the PTY authorize
   * endpoint, which 403s regardless of what the client believes.
   */
  getWorkspaceCapabilities(orgId: number) {
    return this.request<{
      web_ide: boolean;
      cloud_workspaces: boolean;
      /** Plan ceiling. <= 0 means unlimited — check `unlimited` instead. */
      max_workspaces: number;
      used_workspaces: number;
      unlimited: boolean;
      /** Server's verdict on whether a create would succeed right now. */
      can_create: boolean;
    }>("/v1/workspaces/capabilities", { headers: { "X-Org-Id": String(orgId) } });
  }

  /** Stop a workspace by UUID (requires org context). */
  stopWorkspaceById(uuid: string) {
    return this.request<{ ok: boolean }>(`/v1/workspaces/${uuid}/stop`, { method: "POST" });
  }

  /** Delete a workspace by UUID. */
  deleteWorkspaceById(uuid: string) {
    return this.request<{ ok: boolean }>(`/v1/workspaces/${uuid}`, { method: "DELETE" });
  }

  /** Start a stopped workspace by UUID. */
  startWorkspaceById(uuid: string) {
    return this.request<{ ok: boolean }>(`/v1/workspaces/${uuid}/start`, { method: "POST" });
  }

  // ── Admin Auto mode config (AdminAutoModeController, doc 09) ──

  getAutoModeConfig() {
    return this.request<AutoModeConfigResponse>("/v1/admin/auto-mode");
  }

  saveAutoModeConfig(profile: AutoModeProfile, body: AutoModeSaveBody) {
    return this.request<AutoModeVersion>(`/v1/admin/auto-mode/${profile}`, { method: "PUT", body });
  }

  getAutoModeHistory(profile: AutoModeProfile) {
    return this.request<{ profile: AutoModeProfile; data: AutoModeVersion[] }>(
      `/v1/admin/auto-mode/history?profile=${profile}`,
    );
  }

  rollbackAutoModeConfig(profile: AutoModeProfile, version: number) {
    return this.request<AutoModeVersion>(`/v1/admin/auto-mode/${profile}/rollback/${version}`, { method: "POST" });
  }

  getAutoModeProxyStatus() {
    return this.request<AutoModeProxyStatus>("/v1/admin/auto-mode/proxy-status");
  }

  // ── Admin Billing ────────────────────────────────────────────

  adminListPlans() {
    return this.request<Array<{
      id: number;
      code: string;
      name: string;
      account_type: string;
      price_cents: number;
      is_per_seat: boolean;
      max_seats: number;
      stripe_product_id?: string;
      stripe_price_id?: string;
      features: string;
      limits: string;
      is_public: boolean;
      archived: boolean;
    }>>("/v1/admin/plans", { baseUrl: BILLING_URL });
  }

  adminCreatePlan(plan: {
    code: string;
    name: string;
    account_type: string;
    price_cents: number;
    is_per_seat: boolean;
    max_seats: number;
    features: string;
    limits: string;
    is_public: boolean;
  }) {
    return this.request("/v1/admin/plans", { method: "POST", body: plan, baseUrl: BILLING_URL });
  }

  adminListSubscriptions(status?: string, limit = 50, offset = 0) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return this.request<Array<{
      id: number;
      org_id: number;
      plan_id: number;
      status: string;
      seats: number;
      plan?: { code: string; name: string };
    }>>(`/v1/admin/subscriptions?${params}`, { baseUrl: BILLING_URL });
  }

  // ── Admin Usage ──────────────────────────────────────────────

  adminUserUsage(userId: number, period = "30d", page = 1, perPage = 20) {
    const params = new URLSearchParams({ period, page: String(page), per_page: String(perPage) });
    return this.request<{
      user_id: number;
      period: string;
      from: string;
      to: string;
      summary: {
        total_requests: number;
        total_tokens_in: number;
        total_tokens_out: number;
        total_cost_micros: number;
        avg_duration_ms: number;
        error_count: number;
        error_rate: number;
        change: { requests: number; tokens: number };
      };
      by_model: Array<{
        model: string;
        provider: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cost_micros: number;
      }>;
      requests: {
        data: Array<{
          id: number;
          event_id: string;
          model: string;
          provider: string;
          kind: string;
          tokens_in: number;
          tokens_out: number;
          cost_micros: number;
          duration_ms: number;
          status: string;
          occurred_at: string;
        }>;
        total: number;
        page: number;
        per_page: number;
        total_pages: number;
      };
    }>(`/v1/admin/users/${userId}/usage?${params}`);
  }

  // ── Admin Revenue ────────────────────────────────────────────

  /** Get platform revenue overview (super admin). */
  getAdminRevenue() {
    return this.request<{
      mrr: number;
      mrr_cents: number;
      arpu: number;
      churn_rate: number;
      churn_rate_pct: number;
      total_users: number;
      paying_users: number;
      total_seats: number;
      active_subscriptions: number;
      past_due_count: number;
      suspended_count: number;
      canceled_count: number;
      tier_distribution: Record<string, number>;
      revenue_time_series: Array<{
        month: string;
        revenue_cents: number;
        invoice_count: number;
      }>;
    }>("/v1/admin/revenue");
  }

  adminOrgUsage(orgId: number, period = "30d", page = 1, perPage = 20, userId?: number) {
    const params = new URLSearchParams({ period, page: String(page), per_page: String(perPage) });
    if (userId) params.set("user_id", String(userId));
    return this.request<{
      org_id: number;
      period: string;
      from: string;
      to: string;
      filter_user_id: number | null;
      summary: {
        total_requests: number;
        total_tokens_in: number;
        total_tokens_out: number;
        total_cost_micros: number;
        avg_duration_ms: number;
        error_count: number;
        error_rate: number;
        change: { requests: number; tokens: number };
      };
      by_model: Array<{
        model: string;
        provider: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cost_micros: number;
      }>;
      by_user: Array<{
        user_id: number;
        user_name: string;
        user_email: string;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cost_micros: number;
      }>;
      requests: {
        data: Array<{
          id: number;
          event_id: string;
          user_id: number;
          user_name: string;
          model: string;
          provider: string;
          kind: string;
          tokens_in: number;
          tokens_out: number;
          cost_micros: number;
          duration_ms: number;
          status: string;
          occurred_at: string;
        }>;
        total: number;
        page: number;
        per_page: number;
        total_pages: number;
      };
    }>(`/v1/admin/orgs/${orgId}/usage?${params}`);
  }

  // ── File Uploads ────────────────────────────────────────────────

  /**
   * Upload a file using multipart/form-data.
   * Does NOT set Content-Type (browser sets it with boundary).
   */
  async uploadFile<T = unknown>(path: string, file: File, fieldName = "file", _isRetry = false): Promise<T> {
    const token = this.getToken();
    const formData = new FormData();
    formData.append(fieldName, file);

    const res = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (res.status === 401) {
      if (!_isRetry) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          return this.uploadFile<T>(path, file, fieldName, true);
        }
      }
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || err.message || "Upload failed");
    }

    return res.json();
  }

  uploadAvatar(file: File) {
    return this.uploadFile<{ id: number; url: string; mime_type: string; size_bytes: number }>(
      "/v1/uploads/avatar",
      file,
    );
  }

  uploadOrgLogo(orgId: number, file: File) {
    return this.uploadFile<{ id: number; url: string; mime_type: string; size_bytes: number }>(
      `/v1/uploads/org/${orgId}/logo`,
      file,
    );
  }

  deleteUpload(id: number) {
    return this.request<{ deleted: boolean }>(
      `/v1/uploads/${id}`,
      { method: "DELETE" },
    );
  }

  // ── Audit Logs ─────────────────────────────────────────────

  getAuditLogs(orgId: number, params?: { page?: number; per_page?: number; action?: string; actor_id?: number; from?: string; to?: string }) {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.per_page) q.set("per_page", String(params.per_page));
    if (params?.action) q.set("action", params.action);
    if (params?.actor_id) q.set("actor_id", String(params.actor_id));
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{
      data: Array<{
        id: number;
        org_id: number;
        actor_id: number | null;
        actor_name: string | null;
        actor_email: string | null;
        actor_avatar: string | null;
        action: string;
        target_type: string | null;
        target_id: number | null;
        metadata: Record<string, unknown> | null;
        created_at: string;
      }>;
      total: number;
      page: number;
      per_page: number;
      pages: number;
    }>(`/v1/orgs/${orgId}/audit-logs${qs}`);
  }

  getAuditActions(orgId: number) {
    return this.request<string[]>(`/v1/orgs/${orgId}/audit-logs/actions`);
  }

  async exportAuditLogsCsv(orgId: number, params?: { action?: string; from?: string; to?: string }) {
    const q = new URLSearchParams();
    if (params?.action) q.set("action", params.action);
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    const qs = q.toString() ? `?${q.toString()}` : "";
    const token = typeof window !== "undefined" ? localStorage.getItem("mc_token") : null;
    const res = await fetch(`${this.baseUrl}/v1/orgs/${orgId}/audit-logs/export${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Export failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${orgId}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Team Memories ──────────────────────────────────────────

  getTeamMemories(orgId: number, params?: { status?: string; category?: string; since?: string }) {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.category) q.set("category", params.category);
    if (params?.since) q.set("since", params.since);
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{
      data: Array<{
        id: number;
        key: string;
        content: string;
        category: string;
        confidence: number;
        status: string;
        created_by: number | null;
        reviewed_by: number | null;
        created_at: string;
        updated_at: string | null;
      }>;
      count: number;
    }>(`/v1/orgs/${orgId}/team-memories${qs}`);
  }

  createTeamMemory(orgId: number, data: {
    key: string;
    content: string;
    category?: string;
    confidence?: number;
  }) {
    return this.request<{ id: number; key: string; status: string }>(
      `/v1/orgs/${orgId}/team-memories`,
      { method: "POST", body: data },
    );
  }

  updateTeamMemory(orgId: number, memoryId: number, data: {
    status?: string;
    content?: string;
    category?: string;
    confidence?: number;
  }) {
    return this.request<{ id: number; status: string }>(
      `/v1/orgs/${orgId}/team-memories/${memoryId}`,
      { method: "PATCH", body: data },
    );
  }

  deleteTeamMemory(orgId: number, memoryId: number) {
    return this.request<{ deleted: boolean }>(
      `/v1/orgs/${orgId}/team-memories/${memoryId}`,
      { method: "DELETE" },
    );
  }

  // ── Models ────────────────────────────────────────────────

  getModels() {
    return this.request<{
      models: Record<string, Array<{
        id: string;
        display_name: string;
        provider: string;
        tier: string;
        supports_fast: boolean;
        supports_thinking: boolean;
        context_window: number;
        max_output_tokens: number;
        cost_input_micros: number;
        cost_output_micros: number;
      }>>;
      flat: Array<{
        id: string;
        display_name: string;
        provider: string;
        tier: string;
        supports_fast: boolean;
        supports_thinking: boolean;
        context_window: number;
        max_output_tokens: number;
        cost_input_micros: number;
        cost_output_micros: number;
      }>;
    }>(`/v1/models`);
  }

  // ── Team Policy ────────────────────────────────────────────

  getTeamPolicy(orgId: number) {
    return this.request<{
      team_id: string;
      allowed_models: string[];
      blocked_tools: string[];
      require_memory_approval: boolean;
      max_concurrent_sessions: number;
      max_session_duration_secs: number;
      data_residency: {
        code_leaves_machine: boolean;
        responses_stored_cloud: boolean;
        logs_encrypted_at_rest: boolean;
        allowed_regions: string[];
        telemetry_enabled: boolean;
      };
      team_rules: string[];
      sensitive_file_patterns: string[];
      budget_max_usd_per_run?: string;
      budget_max_tokens_per_day?: string;
    }>(`/v1/orgs/${orgId}/policy`);
  }

  updateTeamPolicy(orgId: number, data: Record<string, unknown>) {
    return this.request<{ status: string; updated_keys: string[] }>(
      `/v1/orgs/${orgId}/policy`,
      { method: "PUT", body: data },
    );
  }

  // ── SSO Connections ────────────────────────────────────────

  getSsoConnections(orgId: number) {
    return this.request<Array<{
      id: number;
      protocol: string;
      domain: string;
      is_active: boolean;
      has_scim: boolean;
      idp_name: string | null;
      created_at: string;
    }>>(`/v1/orgs/${orgId}/sso`);
  }

  createSsoConnection(orgId: number, data: {
    protocol: "saml" | "oidc";
    domain: string;
    metadata?: Record<string, unknown>;
  }) {
    return this.request<{ id: number; protocol: string; domain: string; status: string }>(
      `/v1/orgs/${orgId}/sso`,
      { method: "POST", body: data },
    );
  }

  updateSsoConnection(orgId: number, connectionId: number, data: {
    is_active?: boolean;
    metadata?: Record<string, unknown>;
  }) {
    return this.request<{ id: number; status: string }>(
      `/v1/orgs/${orgId}/sso/${connectionId}`,
      { method: "PATCH", body: data },
    );
  }

  deleteSsoConnection(orgId: number, connectionId: number) {
    return this.request<{ deleted: boolean }>(
      `/v1/orgs/${orgId}/sso/${connectionId}`,
      { method: "DELETE" },
    );
  }

  // ── IP Allowlist ───────────────────────────────────────────

  getIpAllowlist(orgId: number) {
    return this.request<{
      rules: Array<{
        id: number;
        org_id: number;
        cidr: string;
        label: string | null;
        created_by: number | null;
        created_by_name: string | null;
        created_at: string;
      }>;
      scope: "api_only" | "api_and_console";
    }>(`/v1/orgs/${orgId}/ip-allowlist`);
  }

  addIpRule(orgId: number, cidr: string, label?: string) {
    return this.request<{ id: number; cidr: string; label: string | null }>(
      `/v1/orgs/${orgId}/ip-allowlist`,
      { method: "POST", body: { cidr, label: label || null } },
    );
  }

  deleteIpRule(orgId: number, ruleId: number) {
    return this.request<{ deleted: boolean }>(
      `/v1/orgs/${orgId}/ip-allowlist/${ruleId}`,
      { method: "DELETE" },
    );
  }

  updateIpScope(orgId: number, scope: "api_only" | "api_and_console") {
    return this.request<{ scope: string }>(
      `/v1/orgs/${orgId}/ip-allowlist/scope`,
      { method: "PATCH", body: { scope } },
    );
  }

  // ── Provider Keys (BYOK) ─────────────────────────────────────

  getProviderKeys(orgId: number) {
    return this.request<{
      provider_keys: Array<{
        id: number;
        provider: string;
        label: string;
        is_active: boolean;
        created_at: string;
      }>;
    }>(`/v1/orgs/${orgId}/provider-keys`);
  }

  addProviderKey(orgId: number, provider: string, key: string, label?: string) {
    return this.request<{ id: number; provider: string; label: string; message: string }>(
      `/v1/orgs/${orgId}/provider-keys`,
      { method: "POST", body: { provider, key, label: label || provider } },
    );
  }

  deleteProviderKey(orgId: number, keyId: number) {
    return this.request<{ message: string }>(
      `/v1/orgs/${orgId}/provider-keys/${keyId}`,
      { method: "DELETE" },
    );
  }

  // ── Webhooks ────────────────────────────────────────────────

  getWebhooks(orgId: number) {
    return this.request<{
      webhooks: Array<{
        id: number;
        url: string;
        label: string | null;
        events: string[] | null;
        is_active: boolean;
        last_triggered_at: string | null;
        failure_count: number;
        created_at: string;
      }>;
    }>(`/v1/orgs/${orgId}/webhooks`);
  }

  createWebhook(orgId: number, url: string, label?: string, events?: string[]) {
    return this.request<{
      id: number;
      url: string;
      secret: string;
      label: string | null;
      events: string[] | null;
    }>(
      `/v1/orgs/${orgId}/webhooks`,
      { method: "POST", body: { url, label: label || null, events: events || null } },
    );
  }

  updateWebhook(orgId: number, webhookId: number, data: { url?: string; label?: string; events?: string[]; is_active?: boolean }) {
    return this.request<{ message: string }>(
      `/v1/orgs/${orgId}/webhooks/${webhookId}`,
      { method: "PATCH", body: data },
    );
  }

  deleteWebhook(orgId: number, webhookId: number) {
    return this.request<{ message: string }>(
      `/v1/orgs/${orgId}/webhooks/${webhookId}`,
      { method: "DELETE" },
    );
  }

  testWebhook(orgId: number, webhookId: number) {
    return this.request<{
      success: boolean;
      status_code?: number;
      error?: string;
      response?: string | null;
    }>(
      `/v1/orgs/${orgId}/webhooks/${webhookId}/test`,
      { method: "POST" },
    );
  }

  // ── Password & Sessions ────────────────────────────────────────

  changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>(
      "/v1/auth/password",
      { method: "PATCH", body: { current_password: currentPassword, new_password: newPassword } },
    );
  }

  getSessions() {
    return this.request<Array<{
      id: number;
      user_agent: string | null;
      ip_address: string | null;
      created_at: string;
      last_used_at: string | null;
      is_current: boolean;
    }>>("/v1/auth/sessions");
  }

  revokeSession(sessionId: number) {
    return this.request<{ message: string }>(
      `/v1/auth/sessions/${sessionId}`,
      { method: "DELETE" },
    );
  }

  revokeAllSessions() {
    return this.request<{ message: string; revoked: number }>(
      "/v1/auth/sessions",
      { method: "DELETE" },
    );
  }

  // ── Notification Preferences ─────────────────────────────────

  getNotificationPrefs() {
    return this.request<{
      security_alerts: boolean;
      usage_alerts: boolean;
      product_updates: boolean;
      team_activity: boolean;
    }>("/v1/auth/notification-preferences");
  }

  updateNotificationPrefs(prefs: {
    security_alerts?: boolean;
    usage_alerts?: boolean;
    product_updates?: boolean;
    team_activity?: boolean;
  }) {
    return this.request<{ message: string }>(
      "/v1/auth/notification-preferences",
      { method: "PATCH", body: prefs },
    );
  }

  // ── API Keys ───────────────────────────────────────────────────

  /** List all non-revoked API keys for an org, newest first. */
  listApiKeys(orgId: number, page = 1, perPage = 10) {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    return this.request<{
      keys: Array<{
        id: number;
        name: string;
        prefix: string;
        scopes: string[];
        limits: ApiKeyLimits;
        expires_at: string | null;
        last_used_at: string | null;
        created_at: string;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
      /** The IP the key allowlist would see for this browser; null if unknown. */
      caller_ip: string | null;
    }>(`/v1/orgs/${orgId}/api-keys?${params}`);
  }

  /** Create a new API key. Returns the plaintext key only once. */
  createApiKey(orgId: number, data: { name?: string; scopes?: string[] } & ApiKeyLimitsInput) {
    return this.request<{
      id: number;
      name: string;
      prefix: string;
      key: string;
      scopes: string[];
      limits: ApiKeyLimits;
      expires_at: string;
      message: string;
    }>(`/v1/orgs/${orgId}/api-keys`, { method: "POST", body: data });
  }

  /**
   * Change a key's limits. Partial: only fields sent change, `null` removes
   * a limit. A 422 carries per-field messages on `ApiError.fields`.
   */
  updateApiKeyLimits(orgId: number, keyId: number, data: ApiKeyLimitsInput) {
    return this.request<{ id: number; limits: ApiKeyLimits; changed: string[] }>(
      `/v1/orgs/${orgId}/api-keys/${keyId}`,
      { method: "PATCH", body: data },
    );
  }

  /** Permanently revoke an API key. */
  revokeApiKey(orgId: number, keyId: number) {
    return this.request<{ message: string }>(
      `/v1/orgs/${orgId}/api-keys/${keyId}`,
      { method: "DELETE" },
    );
  }

  /** Aggregate stats for org API keys. */
  getApiKeyStats(orgId: number) {
    return this.request<{
      total: number;
      active: number;
      expired: number;
      revoked_30d: number;
      never_used: number;
      last_created_at: string | null;
    }>(`/v1/orgs/${orgId}/api-keys/stats`);
  }

  /**
   * Per-key usage for an org (admin/owner). Window defaults to the last 30
   * days server-side and is capped at 90. Revoked keys are included; IDE/JWT
   * traffic is not (it has no key).
   */
  getApiKeyUsage(orgId: number, from?: string, to?: string) {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return this.request<{
      from: string;
      to: string;
      keys: Array<{
        key_id: number;
        prefix: string;
        name: string;
        revoked: boolean;
        requests: number;
        tokens_in: number;
        tokens_out: number;
        cached_tokens: number;
        cost_micros: number;
        last_request_at: string | null;
      }>;
    }>(`/v1/orgs/${orgId}/api-keys/usage${qs ? `?${qs}` : ""}`);
  }

  /** Admin: Impersonate a user */
  impersonateUser(userId: number) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      impersonating: boolean;
      impersonated_by: number;
      user: {
        id: number;
        email: string;
        name: string;
      };
    }>(`/v1/admin/users/${userId}/impersonate`, { method: "POST" });
  }

  // ── Email Change (authenticated) ──────────────────────────────────

  /** Step 1: Verify current password and send verification code to new email. */
  changeEmailInitiate(newEmail: string, password: string, recaptchaToken?: string) {
    return this.request<{
      message: string;
      expires_in_seconds: number;
      keep_other_sessions: boolean;
    }>("/v1/auth/change-email/initiate", {
      method: "POST",
      body: {
        new_email: newEmail,
        password,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      },
    });
  }

  /** Step 2: Confirm email change with verification code. Returns new tokens (C1 fix). */
  changeEmailConfirm(code: string, newEmail: string) {
    return this.request<{
      message: string;
      access_token: string;
      refresh_token: string;
      needs_reverification: boolean;
      old_email: string;
      sessions_invalidated: number;
      grace_period_end: string;
    }>("/v1/auth/change-email/confirm", {
      method: "POST",
      body: { code, new_email: newEmail },
    });
  }

  // ── Email Recovery (unauthenticated) ──────────────────────────────

  /** Step 1: Send a 6-digit code to the user's recovery email. No reCAPTCHA needed. Rate limit: 3/60. */
  sendRecoveryCode(email: string) {
    return this.request<{ message: string }>("/v1/auth/recover-email/send-recovery-code", {
      method: "POST",
      body: { email },
    });
  }

  /** Step 2: Verify identity via password or recovery email code. reCAPTCHA required. Rate limit: 3/15. */
  recoverEmailVerifyIdentity(params: {
    email: string;
    password?: string;
    recovery_code?: string;
    recaptcha_token?: string;
  }) {
    return this.request<{
      identity_token: string;
      expires_in_seconds: number;
      user_id: number;
      old_email: string;
      method: "password" | "recovery_email";
    }>("/v1/auth/recover-email/verify-identity", {
      method: "POST",
      body: params,
    });
  }

  /** Check verify-email cooldown status (rate limit: 30/60). */
  verifyEmailStatus(email?: string) {
    const qs = email ? `?email=${encodeURIComponent(email)}` : "";
    return this.request<{
      verified: boolean;
      can_resend: boolean;
      resend_cooldown_seconds?: number;
      expires_at?: string;
    }>(`/v1/auth/verify-email/status${qs}`);
  }

  /** Step 3: Send verification code to new email (requires identity_token from step 2). */
  recoverEmailSendCode(identityToken: string, newEmail: string) {
    return this.request<{
      message: string;
      expires_in_seconds: number;
    }>("/v1/auth/recover-email/send-code", {
      method: "POST",
      body: { identity_token: identityToken, new_email: newEmail },
    });
  }

  /** Step 3: Confirm code and update email (requires identity_token from step 1). */
  recoverEmailConfirm(identityToken: string, code: string, newEmail: string) {
    return this.request<{
      message: string;
      access_token: string;
      refresh_token: string;
      user: { id: number; email: string; name: string };
      sessions_invalidated: number;
      grace_period_end: string;
    }>("/v1/auth/recover-email/confirm", {
      method: "POST",
      body: { identity_token: identityToken, code, new_email: newEmail },
    });
  }

  // ── Recovery Email CRUD (authenticated) ───────────────────────────

  /** Get current recovery email (masked). */
  getRecoveryEmail() {
    return this.request<{
      recovery_email: string | null;
      has_recovery_email: boolean;
    }>("/v1/auth/recovery-email");
  }

  /** Set or change recovery email (requires password re-auth). */
  setRecoveryEmail(recoveryEmail: string, password: string) {
    return this.request<{
      message: string;
      recovery_email: string;
    }>("/v1/auth/recovery-email", {
      method: "PUT",
      body: { recovery_email: recoveryEmail, password },
    });
  }

  // ── Backup Codes (P1) — COMMENTED OUT for future implementation ──
  //
  // getBackupCodes() {
  //   return this.request<{
  //     codes: Array<{ prefix: string; used_at: string | null }>;
  //     total: number;
  //     used: number;
  //     remaining: number;
  //     generated_at: string | null;
  //   }>("/v1/auth/backup-codes");
  // }
  //
  // generateBackupCodes(password: string) {
  //   return this.request<{
  //     codes: string[];
  //     message: string;
  //   }>("/v1/auth/backup-codes/generate", {
  //     method: "POST",
  //     body: { password },
  //   });
  // }

  // ── Invoices ───────────────────────────────────────────────────────

  /** List invoices for the current org. */
  listInvoices(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    from?: string;
    to?: string;
  }) {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.per_page) q.set("per_page", String(params.per_page));
    if (params?.status) q.set("status", params.status);
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{
      invoices: Array<InvoiceItem>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/v1/billing/invoices${qs}`);
  }

  /** Get a single invoice with line items. */
  getInvoice(id: number) {
    return this.request<{ invoice: InvoiceDetail }>(
      `/v1/billing/invoices/${id}`
    );
  }

  /** Get invoice PDF download URL. */
  getInvoicePdfUrl(id: number) {
    return this.request<{ pdf_url: string }>(
      `/v1/billing/invoices/${id}/pdf`
    );
  }

  /** Admin: List all invoices (filterable). */
  adminListInvoices(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    search?: string;
  }) {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.per_page) q.set("per_page", String(params.per_page));
    if (params?.status) q.set("status", params.status);
    if (params?.search) q.set("search", params.search);
    const qs = q.toString() ? `?${q.toString()}` : "";
    return this.request<{
      invoices: Array<InvoiceItem>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/v1/admin/invoices${qs}`);
  }

  /** Admin: Get any invoice with line items. */
  adminGetInvoice(id: number) {
    return this.request<{ invoice: InvoiceDetail }>(
      `/v1/admin/invoices/${id}`
    );
  }

  /** Admin: Create a manual one-off invoice. */
  adminCreateInvoice(data: {
    org_id: number;
    invoice_type: string;
    currency?: string;
    notes?: string;
    line_items: Array<{
      description: string;
      quantity: number;
      unit_price_cents: number;
      total_cents: number;
      line_type: string;
    }>;
  }) {
    return this.request<{ invoice: InvoiceDetail }>(
      "/v1/admin/invoices",
      { method: "POST", body: data }
    );
  }

  /** Admin: Void an invoice. */
  adminVoidInvoice(id: number) {
    return this.request<{ message: string }>(
      `/v1/admin/invoices/${id}/void`,
      { method: "POST" }
    );
  }

  /** Admin: Issue refund + credit note. */
  adminRefundInvoice(id: number) {
    return this.request<{ message: string }>(
      `/v1/admin/invoices/${id}/refund`,
      { method: "POST" }
    );
  }

  /** Admin: Invoice statistics. */
  adminInvoiceStats() {
    return this.request<{
      total_issued_this_month: number;
      total_paid_this_month: number;
      total_overdue: number;
      overdue_amount_cents: number;
      total_revenue_cents_this_month: number;
      avg_days_to_payment: number;
      by_status: Record<string, number>;
    }>("/v1/admin/invoices/stats");
  }

  // ── Email Campaigns ──────────────────────────────────────────────

  /** Admin: List all email campaigns. */
  adminListCampaigns(page = 1, perPage = 20) {
    return this.request<{ data: EmailCampaign[]; total: number; page: number; per_page: number }>(
      `/v1/admin/campaigns?page=${page}&per_page=${perPage}`
    );
  }

  /** Admin: Get campaign details. */
  adminGetCampaign(id: number) {
    return this.request<{ data: EmailCampaign & { recipient_count: number } }>(
      `/v1/admin/campaigns/${id}`
    );
  }

  /** Admin: Create campaign (draft). Supports attachment upload via FormData. */
  async adminCreateCampaign(data: {
    subject: string;
    body_html: string;
    segment_type: string;
    segment_value?: string | null;
    attachment?: File | null;
  }): Promise<{ data: EmailCampaign }> {
    const token = this.getToken();
    const formData = new FormData();
    formData.append("subject", data.subject);
    
    // Base64 encode the HTML body to bypass WAF XSS rules
    const b64Body = Buffer.from(data.body_html).toString("base64");
    formData.append("body_html_b64", b64Body);
    
    formData.append("segment_type", data.segment_type);
    if (data.segment_value) formData.append("segment_value", data.segment_value);
    if (data.attachment) formData.append("attachment", data.attachment);

    const res = await fetch(`${this.baseUrl}/v1/admin/campaigns`, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || "Failed to create campaign");
    }
    return res.json();
  }

  /** Admin: Update campaign. */
  adminUpdateCampaign(id: number, data: Partial<{
    subject: string;
    body_html: string;
    segment_type: string;
    segment_value: string | null;
  }>) {
    const payload: Record<string, unknown> = { ...data };
    if (payload.body_html !== undefined) {
      payload.body_html_b64 = Buffer.from(payload.body_html as string).toString("base64");
      delete payload.body_html;
    }
    return this.request<{ data: EmailCampaign }>(
      `/v1/admin/campaigns/${id}`,
      { method: "PUT", body: JSON.stringify(payload) }
    );
  }

  /** Admin: Send or resend campaign (only to new recipients). */
  adminSendCampaign(id: number) {
    return this.request<{
      message: string;
      sent: number;
      failed: number;
      total_sent_all_time: number;
      send_count: number;
    }>(`/v1/admin/campaigns/${id}/send`, { method: "POST" });
  }

  /** Admin: Delete a campaign. */
  adminDeleteCampaign(id: number) {
    return this.request<{ message: string }>(
      `/v1/admin/campaigns/${id}`,
      { method: "DELETE" }
    );
  }

  /** Admin: Preview campaign recipients (with new-vs-already-sent breakdown). */
  adminPreviewCampaignRecipients(id: number) {
    return this.request<{
      total_matching: number;
      already_sent: number;
      new_recipients: number;
      sample: Array<{ id: number; email: string; name: string }>;
    }>(`/v1/admin/campaigns/${id}/preview`);
  }

  /** Admin: Available campaign segments. */
  adminCampaignSegments() {
    return this.request<{
      segments: Array<{
        type: string;
        label: string;
        values: Array<{ code: string; name: string }> | null;
      }>;
    }>("/v1/admin/campaigns/meta/segments");
  }

  // ── Abuse Detection ──────────────────────────────────────────────

  /** Admin: List abuse alerts. */
  adminAbuseAlerts(status: "pending" | "all" = "pending") {
    return this.request<{
      alerts: AbuseAlert[];
      count: number;
    }>(`/v1/admin/abuse/alerts?status=${status}`);
  }

  /** Admin: Trigger abuse scan. */
  adminAbuseScan() {
    return this.request<{
      scanned: boolean;
      alerts_created: number;
      alerts: AbuseAlert[];
    }>("/v1/admin/abuse/scan", { method: "POST" });
  }

  /** Admin: Confirm abuse alert (suspends accounts + sends email). */
  adminConfirmAbuse(id: number) {
    return this.request<{
      status: string;
      subscriptions_suspended: number;
      emails_sent: number;
    }>(`/v1/admin/abuse/alerts/${id}/confirm`, { method: "POST" });
  }

  /** Admin: Dismiss abuse alert. */
  adminDismissAbuse(id: number) {
    return this.request<{ status: string }>(`/v1/admin/abuse/alerts/${id}/dismiss`, { method: "POST" });
  }
}

// ── Invoice Types ─────────────────────────────────────────────────

export type InvoiceStatus = "draft" | "issued" | "paid" | "overdue" | "cancelled" | "void" | "refunded" | "uncollectible";

export type InvoiceItem = {
  id: number;
  invoice_number: string;
  status: InvoiceStatus;
  total_cents: number;
  currency: string;
  period_start: string;
  period_end: string;
  issued_at: string | null;
  paid_at: string | null;
  pdf_url: string | null;
  invoice_type: "subscription" | "credit_purchase" | "adjustment" | "one_off";
};

export type InvoiceLineItem = {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
  tax_cents: number;
  line_type: "subscription" | "credit" | "seat" | "addon" | "discount" | "tax";
};

export type InvoiceDetail = InvoiceItem & {
  subtotal_cents: number;
  tax_cents: number;
  tax_rate: number | null;
  amount_paid_cents: number;
  amount_remaining_cents: number;
  due_at: string | null;
  notes: string | null;
  stripe_invoice_id: string | null;
  line_items: InvoiceLineItem[];
  org?: {
    id: number;
    name: string;
    billing_email: string;
  };
};

// ── Email Campaign Types ──────────────────────────────────────────

export type EmailCampaign = {
  id: number;
  subject: string;
  body_html: string;
  segment_type: "all" | "plan" | "status";
  segment_value: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  status: "draft" | "sending" | "sent" | "failed";
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  send_count: number;
  sent_by: number;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string | null;
};

const BILLING_URL = process.env.NEXT_PUBLIC_BILLING_URL || "http://billing.monkeyscode.local";

export const api = new ApiClient(API_URL);

// ── Abuse Detection Types ────────────────────────────────────────

// ── Feedback survey types (mirror FeedbackSurveyController / AdminFeedbackController) ──

export type FeedbackOption = { value: string; label: string };

export type FeedbackSurveyPayload = {
  survey: {
    status: "sent" | "completed";
    completed: boolean;
    completed_at: string | null;
    first_name: string | null;
  };
  options: {
    products: FeedbackOption[];
    issue_areas: FeedbackOption[];
    max_text: number;
  };
};

export type FeedbackSurveyAnswers = {
  overall_rating: number;
  nps_score: number | null;
  products_used: string[];
  had_issue: boolean | null;
  issue_area: string | null;
  issue_details: string | null;
  improvements: string | null;
  contact_ok: boolean;
};

export type AdminFeedbackSummary = {
  range: { from: string | null; to: string | null; group_by: "day" | "month" };
  funnel: {
    sent: number;
    opened: number;
    completed: number;
    open_rate: number;
    completion_rate: number;
  };
  answers: {
    responses: number;
    avg_rating: number | null;
    rating_distribution: Record<string, number>;
    nps: {
      score: number | null;
      answered: number;
      promoters: number;
      passives: number;
      detractors: number;
      distribution: Record<string, number>;
    };
    issues: { count: number; rate: number; by_area: Record<string, number> };
    products_used: Record<string, number>;
    with_improvements: number;
    contact_ok: number;
  };
  timeseries: Array<{
    bucket: string;
    sent: number;
    completed: number;
    avg_rating: number | null;
    issues: number;
  }>;
  labels: { products: Record<string, string>; issue_areas: Record<string, string> };
};

export type AdminFeedbackEntry = {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  status: "sent" | "completed";
  sent_at: string | null;
  opened_at: string | null;
  completed_at: string | null;
  overall_rating: number | null;
  nps_score: number | null;
  had_issue: boolean | null;
  issue_area: string | null;
  improvements_preview: string | null;
};

export type AdminFeedbackResponses = {
  total: number;
  limit: number;
  offset: number;
  entries: AdminFeedbackEntry[];
};

export type AdminFeedbackResponseDetail = {
  id: number;
  survey_key: string;
  status: "sent" | "completed";
  sent_at: string | null;
  opened_at: string | null;
  completed_at: string | null;
  overall_rating: number | null;
  nps_score: number | null;
  products_used: string[];
  had_issue: boolean | null;
  issue_area: string | null;
  issue_details: string | null;
  improvements: string | null;
  contact_ok: boolean;
  user: {
    id: number;
    name: string;
    email: string;
    status: string;
    created_at: string | null;
    last_login_at: string | null;
  };
  labels: { products: Record<string, string>; issue_areas: Record<string, string> };
};

// ── Admin git integration types ──────────────────────────────────

export type AdminGitSummary = {
  window_days: number;
  since: string;
  credentials: {
    active: number;
    inactive: number;
    never_used: number;
    installations: number;
  };
  activity: {
    connects: number;
    disconnects: number;
    mints: number;
    rate_limited: number;
    reconciled: number;
    aborts: number;
  };
};

export type AdminGitHealth = {
  last_abort_at: string | null;
  last_reconcile_at: string | null;
  /** True when a reconcile abort is the MOST RECENT outcome — an active alarm. */
  abort_is_latest: boolean;
};

export type AdminGitEvent = {
  id: number;
  action: string;
  actor_id: number | null;
  actor_name: string | null;
  actor_email: string | null;
  target_type: string | null;
  target_id: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export type AdminGitEvents = {
  total: number;
  limit: number;
  offset: number;
  entries: AdminGitEvent[];
  available_actions: string[];
};

/** One row of the platform compliance log (`audit_logs`). */
export type AdminAuditLogEntry = AdminGitEvent & {
  org_id: number | null;
};

export type AdminAuditLogResponse = {
  total: number;
  limit: number;
  offset: number;
  entries: AdminAuditLogEntry[];
};

// ── Auto mode config (mirror AdminAutoModeController) ─────────────

export type AutoModeProfile = "balanced" | "max_quality";
export type AutoModeFamily = "capuchin" | "frontier";

export type AutoModeThresholds = {
  fail_attempts_before_escalation: number;
  loop_identical_calls: number;
  loop_turns_without_change: number;
  escalations_per_task_cap: number;
  plan_and_review_every_task: boolean;
  thinking_budget_execute: number;
  thinking_budget_plan: number;
};

export type AutoModeVersion = {
  profile: AutoModeProfile;
  version: number;
  is_current: boolean;
  roles: Record<string, { id: number; model_id: string | null; display_name: string | null; is_active: boolean }>;
  thresholds: AutoModeThresholds;
  note: string | null;
  updated_by: { id: number; name: string; email: string } | null;
  created_at: string;
};

export type AutoModeEligibleModel = {
  id: number;
  model_id: string;
  display_name: string;
  provider: string;
  sort_order: number;
  selectable: boolean;
  reason: string | null;
  canonical: string | null;
  served_by: string | null;
  prices: {
    input_micros: number | null;
    output_micros: number | null;
    cache_read_micros: number | null;
    cache_write_micros: number | null;
    long_context_tier: boolean;
  } | null;
};

export type AutoModeConfigResponse = {
  profiles: Record<AutoModeProfile, AutoModeVersion | null>;
  roles: Record<AutoModeFamily, string[]>;
  eligible: Record<AutoModeFamily, AutoModeEligibleModel[]>;
  threshold_bounds: Record<string, [number, number]>;
  threshold_flags: string[];
  proxy_reachable: boolean;
};

export type AutoModeSaveBody = {
  expected_version: number;
  roles: Record<string, number>;
  thresholds: AutoModeThresholds;
  note?: string;
};

export type AutoModeProxyStatus = {
  reachable: boolean;
  source?: "platform" | "redis" | "builtin" | null;
  applied_at?: string | null;
  last_error?: string | null;
  last_fetch?: string | null;
  applied_versions?: Partial<Record<AutoModeProfile, number>>;
  saved_versions: Partial<Record<AutoModeProfile, number>>;
  in_sync: boolean;
};

export type AbuseAlert = {
  id: number;
  alert_type: string;
  user_ids: number[];
  emails: string[];
  org_ids: number[];
  name: string;
  reason: string;
  score: number;
  status: "pending" | "confirmed" | "dismissed";
  created_at: string;
  resolved_at: string | null;
  resolved_by: string;
};
